import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, addDoc, serverTimestamp } from 'firebase/firestore';
// We only need the database (db) for this method, not storage.
import { db } from '../firebase';
import './GrnPage.css';

// This helper function takes a file and converts it into a Base64 text string.
const toBase64 = file => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = error => reject(error);
});

const AddGrnPage = () => {
    // State for overall GRN details
    const [purchasePrice, setPurchasePrice] = useState('');
    const [purchaseDate, setPurchaseDate] = useState('');
    const [addedBy, setAddedBy] = useState('');
    const [billFile, setBillFile] = useState(null);

    // State for the list of products in the GRN (starts with one empty row)
    const [grnItems, setGrnItems] = useState([{ productId: '', productName: '', quantity: '', unit: '' }]);

    // State to hold all available products for dropdowns
    const [products, setProducts] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    const navigate = useNavigate();

    // Fetch all products from your 'products' collection on component mount
    useEffect(() => {
        const fetchProducts = async () => {
            const productsCollectionRef = collection(db, "products");
            try {
                const data = await getDocs(productsCollectionRef);
                setProducts(data.docs.map((doc) => ({ ...doc.data(), id: doc.id })));
            } catch (error) {
                console.error("Error fetching products: ", error);
                alert("Could not load product list. Please make sure you have a 'products' collection.");
            }
        };
        fetchProducts();
    }, []);

    // Handles changes in any field of a specific item row
    const handleItemChange = (index, field, value) => {
        const newItems = [...grnItems];
        newItems[index][field] = value;
        if (field === 'productId') {
            const selectedProduct = products.find(p => p.id === value);
            newItems[index]['productName'] = selectedProduct ? selectedProduct.name : '';
        }
        setGrnItems(newItems);
    };

    // Adds a new, empty item row to the form
    const handleAddItem = () => {
        setGrnItems([...grnItems, { productId: '', productName: '', quantity: '', unit: '' }]);
    };

    // Removes an item row from the form
    const handleRemoveItem = (index) => {
        if (grnItems.length > 1) { // Prevent removing the last row
            const newItems = grnItems.filter((_, i) => i !== index);
            setGrnItems(newItems);
        }
    };

    const handleFileChange = (e) => {
        if (e.target.files[0]) {
            setBillFile(e.target.files[0]);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        if (!purchasePrice || !purchaseDate || !addedBy || !billFile || grnItems.some(item => !item.productId || !item.quantity || !item.unit)) {
            alert("Please fill out all fields, upload a bill, and complete all product lines.");
            setIsSubmitting(false);
            return;
        }

        try {
            // 1. Convert the selected file into a Base64 text string.
            const billAsBase64 = await toBase64(billFile);

            // 2. Prepare the complete data object for Firestore
            const grnData = {
                items: grnItems.map(item => ({
                    productId: item.productId,
                    productName: item.productName,
                    quantityReceived: Number(item.quantity),
                    unit: item.unit
                })),
                totalPurchasePrice: Number(purchasePrice),
                purchaseDate: purchaseDate,
                addedBy: addedBy,
                billData: billAsBase64,
                billFileName: billFile.name,
                createdAt: serverTimestamp()
            };
            
            // 3. Add the document to the 'grns' collection in Firestore
            const grnsCollectionRef = collection(db, "grns");
            await addDoc(grnsCollectionRef, grnData);

            alert('GRN and bill data saved successfully!');
            navigate('/grns');

        } catch (error) {
            console.error("!!!!!!!! AN ERROR OCCURRED !!!!!!!!");
            console.error("FULL ERROR OBJECT:", error);
            alert(`Failed to create GRN. Check the developer console (F12) for details. Error: ${error.message}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="form-container-grn">
            <h1>Add Goods Received Note (GRN)</h1>
            <form onSubmit={handleSubmit}>
                <div className="form-section-grn">
                    <h2>GRN Details</h2>
                    <div className="form-group-grn"><label>Total Purchase Price (₹)</label><input type="number" min="0" value={purchasePrice} onChange={(e) => setPurchasePrice(e.target.value)} required /></div>
                    <div className="form-group-grn"><label>Purchase On Date</label><input type="date" value={purchaseDate} onChange={(e) => setPurchaseDate(e.target.value)} required /></div>
                    <div className="form-group-grn"><label>Added By</label><input type="text" placeholder="Your Name" value={addedBy} onChange={(e) => setAddedBy(e.target.value)} required /></div>
                    <div className="form-group-grn"><label>Upload Bill/Invoice</label><input type="file" onChange={handleFileChange} accept="image/*,.pdf" required /></div>
                </div>

                <div className="form-section-grn">
                    <h2>Products Received</h2>
                    {grnItems.map((item, index) => (
                        <div key={index} className="grn-item-row">
                            <div className="form-group-grn"><label>Product</label><select value={item.productId} onChange={(e) => handleItemChange(index, 'productId', e.target.value)} required><option value="" disabled>-- Choose a product --</option>{products.map((p) => (<option key={p.id} value={p.id}>{p.name}</option>))}</select></div>
                            <div className="form-group-grn"><label>Quantity</label><input type="number" min="1" value={item.quantity} onChange={(e) => handleItemChange(index, 'quantity', e.target.value)} required /></div>
                            <div className="form-group-grn"><label>Unit</label><input type="text" placeholder="e.g., pcs, kg, box" value={item.unit} onChange={(e) => handleItemChange(index, 'unit', e.target.value)} required /></div>
                            <button type="button" className="remove-item-button-grn" onClick={() => handleRemoveItem(index)}>Remove</button>
                        </div>
                    ))}
                    <button type="button" className="add-item-button-grn" onClick={handleAddItem}>+ Add Another Product</button>
                </div>
                
                <button type="submit" className="submit-button-grn" disabled={isSubmitting}>{isSubmitting ? 'Saving...' : 'Save GRN'}</button>
            </form>
        </div>
    );
};

export default AddGrnPage;